import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { blogCategoryEdit, blogCategoryUpdate } from './proxyurls';
import Layout from './layout';
import Select from 'react-select';

const statusOptions = [
  { value: 1, label: 'Active' },
  { value: 0, label: 'Inactive' },
];

function EditBlogCategory() {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    status: 0,
  });
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await axios.get(blogCategoryEdit(id), {
          withCredentials: true,
        });
        const data = response.data;
        const category = data?.category || data?.data || data;
        setFormData({
          name: category.name || category.title || '',
          slug: category.slug || '',
          description: category.description || '',
          status: category.status !== undefined ? category.status : 0,
        });
      } catch (error) {
        console.error('Error fetching category:', error);
        setErrorMessage(
          error.response?.data?.error || 'Error fetching category details'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      setFormData((prev) => ({
        ...prev,
        name: value,
        slug: generateSlug(value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      status: selectedOption ? selectedOption.value : 0,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (!formData.slug?.trim()) errors.slug = 'Slug is required';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    try {
      await axios.put(blogCategoryUpdate(id), formData, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      setSuccessMessage('Category updated successfully');
      localStorage.setItem('successMessage', 'Category updated successfully');
      setTimeout(() => navigate('/manage-blog-categories'), 1000);
    } catch (error) {
      console.error('Error updating category:', error);
      setErrorMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          'An error occurred'
      );
    }
  };

  const handleCancel = () => {
    navigate('/manage-blog-categories');
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-4">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '8px' }}>
        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}
        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}
        <h3 className="py-3 px-3 border-bottom">Edit Blog Category</h3>
        <form onSubmit={handleSubmit} className="py-3 px-3">
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                id="name"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {formErrors.name && (
                <div className="text-danger">{formErrors.name}</div>
              )}
            </div>
            <div className="col-md-6">
              <label htmlFor="slug" className="form-label">
                Slug
              </label>
              <input
                type="text"
                id="slug"
                className="form-control"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
              />
              {formErrors.slug && (
                <div className="text-danger">{formErrors.slug}</div>
              )}
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-12">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                className="form-control"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of this category"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <Select
                id="status"
                options={statusOptions}
                value={statusOptions.find((o) => o.value === formData.status)}
                onChange={handleSelectChange}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <button type="submit" className="btn btn-primary">
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary ms-2"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default EditBlogCategory;
